import { useCallback, useRef, useState, type ReactNode } from "react";

type NewMessage =
  | { from: "user"; text: string }
  | { from: "bot"; lines: readonly ReactNode[]; isError?: boolean };

// `isTyped`: written during this visit, so the bot types it out.
export type TurnMessage = NewMessage & { id: number; isTyped: boolean };

// The conversation inside one step: the user's attempts and the bot's
// replies, errors included. Kept for the session only; once the step is done
// the thread shows the saved answer instead.
export const useTurnMessages = (initial: readonly NewMessage[] = []) => {
  const nextId = useRef(initial.length);
  const [messages, setMessages] = useState<TurnMessage[]>(() =>
    initial.map((message, id) => ({ ...message, id, isTyped: false })),
  );

  const add = useCallback((message: NewMessage) => {
    const id = nextId.current++;
    setMessages((list) => [...list, { ...message, id, isTyped: true }]);
  }, []);

  const addUser = useCallback(
    (text: string) => add({ from: "user", text }),
    [add],
  );
  const addBot = useCallback(
    (lines: readonly ReactNode[]) => add({ from: "bot", lines }),
    [add],
  );
  const addError = useCallback(
    (message: string) => add({ from: "bot", lines: [message], isError: true }),
    [add],
  );

  return { messages, addUser, addBot, addError };
};
